import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UploadCloud, Layers, Wand2, Sparkles, Type, ScanText, X, FileText, MessageSquare, Send } from "lucide-react";
import { VoiceButton } from "@/components/VoiceButton";
import { AiAvatar } from "@/components/AiAvatar";
import { API } from "@/lib/api";

const IMAGE_ACTIONS = [
  { key: "capas", label: "Reconstruir en capas", desc: "OCR + IA separa texto, logos y foto en capas editables", icon: Layers },
  { key: "editar", label: "Abrir en editor", desc: "Edita directamente: fondo, texto e IA, sin separar capas", icon: Wand2 },
  { key: "editar2", label: "Editar aquí mismo", desc: "Abre tu lienzo para editarlo a tu gusto", icon: Wand2 },
  { key: "cuentame", label: "Cuéntame qué puedo hacer por ti", desc: "Escribe qué quieres y la IA lo hace por ti", icon: MessageSquare },
  { key: "ia", label: "Mejorar / limpiar con IA", desc: "Nano Banana mejora y limpia la imagen", icon: Sparkles },
  { key: "ocr", label: "Extraer texto (OCR)", desc: "Devuelve el texto detectado", icon: Type },
];
const PDF_ACTIONS = [
  { key: "capas", label: "Reconstruir en capas (todas)", desc: "Cada página en capas editables", icon: Layers },
  { key: "editar", label: "Editar PDF (todas las páginas)", desc: "Abre el PDF completo en el editor multipágina", icon: Wand2 },
  { key: "editar2", label: "Editar aquí mismo", desc: "Abre tu lienzo para editarlo a tu gusto", icon: Wand2 },
  { key: "cuentame", label: "Cuéntame qué puedo hacer por ti", desc: "Escribe qué quieres y la IA lo hace por ti", icon: MessageSquare },
  { key: "scan", label: "Escáner: analizar y corregir", desc: "OCR + IA corrige tipografía y errores", icon: ScanText },
  { key: "ia", label: "Mejorar con IA", desc: "Nano Banana mejora la imagen", icon: Sparkles },
];

/** Zona de subida + acciones. La usan el panel admin y el panel de usuarios. */
export const UploadWorkspace = ({ client, tokenKey, jobsPath = "/jobs", onDone }) => {
  const [upload, setUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [cmdMode, setCmdMode] = useState(false);
  const [cmdText, setCmdText] = useState("");
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await client.post("/upload", fd);
      setUpload(data);
      toast.success(`Archivo listo: ${data.filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir el archivo");
    } finally { setUploading(false); }
  };

  const runAction = async (action, extra = {}) => {
    if (action === "cuentame" && !cmdMode) { setCmdMode(true); return; }
    setBusy(action);
    try {
      const { data } = await client.post(`/upload/${upload.upload_id}/action`, { action, ...extra });
      if (data.editor_url && ["editar", "editar2", "cuentame"].includes(action)) {
        navigate(data.editor_url.replace(/^https?:\/\/[^/]+/, ""));
        return;
      }
      setResult({ action, ...data });
      toast.success("Listo.");
      setUpload(null); setCmdMode(false); setCmdText("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo completar la acción");
    } finally { setBusy(null); }
  };

  const reset = () => { setUpload(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };
  const actions = upload?.kind === "pdf" ? PDF_ACTIONS : IMAGE_ACTIONS;

  return (
    <>
      {!upload && (
        <label data-testid="upload-dropzone"
          className="block max-w-2xl border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors"
          style={{ borderColor: "#2A2A32" }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); doUpload(e.dataTransfer.files?.[0]); }}>
          {uploading ? (
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-red-400" />
          ) : (
            <>
              <UploadCloud className="w-12 h-12 mx-auto text-slate-500 mb-3" />
              <div className="text-slate-200 font-semibold">Arrastra aquí tu imagen o PDF</div>
              <div className="text-slate-500 text-sm mt-1">o haz clic para elegir (PNG, JPG, WEBP, PDF)</div>
            </>
          )}
          <input ref={fileRef} data-testid="upload-input" type="file" accept=".png,.jpg,.jpeg,.webp,.pdf"
            className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
        </label>
      )}

      {upload && (
        <div className="max-w-2xl">
          <div className="flex items-center justify-between mb-4 p-3 rounded-lg border" style={{ borderColor: "#1B1B21", background: "rgba(13,17,26,0.72)" }}>
            <div className="flex items-center gap-2 text-slate-200 text-sm">
              <FileText className="w-4 h-4 text-red-400" /> {upload.filename}
              <span className="text-slate-500">({upload.kind === "pdf" ? "PDF" : "Imagen"})</span>
            </div>
            <button data-testid="upload-reset" onClick={reset} className="text-slate-400 hover:text-rose-400"><X className="w-4 h-4" /></button>
          </div>
          <div className="text-slate-300 font-semibold mb-3">¿Qué quieres hacer con este archivo?</div>
          {cmdMode && (
            <div className="mb-4 p-3 rounded-xl border" style={{ borderColor: "#2A2A32", background: "rgba(13,17,26,0.72)" }} data-testid="cmd-box">
              <div className="text-slate-300 text-sm mb-3 flex items-center gap-3">
                <AiAvatar size={46} busy={busy === "cuentame"} />
                Cuéntame qué quieres que haga (escríbelo o dímelo por voz)
              </div>
              <div className="flex items-start gap-2">
                <textarea data-testid="cmd-input" rows={2} value={cmdText} onChange={(e) => setCmdText(e.target.value)}
                  placeholder="Ej. cámbiale el nombre a Juan Pérez; quita el fondo y deja solo el texto; mejora la nitidez…"
                  className="flex-1 bg-[#08080C] border border-[#1B1B21] text-slate-100 text-sm rounded-md px-2 py-1.5 resize-none" />
                <VoiceButton testId="cmd-voice" className="w-10 h-10 shrink-0"
                  url={`${API}/upload/voice`}
                  onText={(t) => { setCmdText(t); toast.success(`Te entendí: «${t}»`); }} />
              </div>
              <div className="flex gap-2 mt-2">
                <button data-testid="cmd-send" onClick={() => cmdText.trim() ? runAction("cuentame", { instruction: cmdText }) : toast.error("Escribe qué quieres hacer")}
                  disabled={!!busy}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gradient-to-r from-red-600 to-rose-700 text-white text-sm disabled:opacity-50">
                  {busy === "cuentame" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Hazlo
                </button>
                <button data-testid="cmd-cancel" onClick={() => { setCmdMode(false); setCmdText(""); }} className="px-3 py-1.5 rounded-md border border-[#1B1B21] text-slate-300 text-sm">Cancelar</button>
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {actions.map((a) => (
              <button key={a.key} data-testid={`upload-action-${a.key}`} onClick={() => runAction(a.key)} disabled={!!busy}
                className="text-left p-4 rounded-xl border hover:border-red-500/60 transition-colors disabled:opacity-40"
                style={{ borderColor: "#1B1B21", background: "rgba(13,17,26,0.72)" }}>
                <div className="flex items-center gap-2 text-slate-100 font-semibold">
                  {busy === a.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <a.icon className="w-4 h-4 text-red-400" />}
                  {a.label}
                </div>
                <div className="text-slate-500 text-xs mt-1">{a.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className="max-w-2xl mt-6 p-4 rounded-xl border" style={{ borderColor: "#1B1B21", background: "rgba(13,17,26,0.72)" }} data-testid="upload-result">
          {result.message && (
            <div className="text-slate-200 text-sm mb-2">
              {result.message}
              {result.action === "capas" && (
                <button className="text-red-400 underline ml-2" onClick={() => navigate(jobsPath)}>Ver Trabajos</button>
              )}
            </div>
          )}
          {result.image_base64 && (
            <div data-testid="result-image-block">
              <div className="text-emerald-400 text-sm mb-2">✅ Imagen lista con tu cambio aplicado</div>
              <img data-testid="result-image" src={result.image_base64} alt="resultado" className="max-w-full rounded-lg border" style={{ borderColor: "#1B1B21" }} />
              <a href={result.image_base64} download="resultado.png" className="inline-block mt-2 text-red-400 underline text-sm">Descargar PNG</a>
            </div>
          )}
          {result.text && <pre className="text-slate-200 text-sm whitespace-pre-wrap">{result.text || "(sin texto)"}</pre>}
          {(result.original || result.corrected) && (
            <div className="text-sm space-y-2">
              <div><span className="text-slate-500">Original (OCR):</span><pre className="text-slate-300 whitespace-pre-wrap">{result.original}</pre></div>
              <div><span className="text-emerald-400">Corregido (IA):</span><pre className="text-slate-100 whitespace-pre-wrap">{result.corrected}</pre></div>
            </div>
          )}
          {!result.message && !result.image_base64 && !result.text && !result.original && (
            <div className="text-slate-400 text-sm">Listo.</div>
          )}
        </div>
      )}
    </>
  );
};

export default UploadWorkspace;
